/**
 * Classification Mode Toggle
 * Shared mode selector for tariff classifier (quick / advanced).
 *
 * variant="card"    — Large explanatory cards used on the Direct tab.
 * variant="compact" — Inline segmented control used above the upload zone.
 */
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Speed as QuickIcon,
  Science as AdvancedIcon,
} from '@mui/icons-material';

export type ClassificationMode = 'quick' | 'advanced';

interface ClassificationModeToggleProps {
  value: ClassificationMode;
  onChange: (mode: ClassificationMode) => void;
  variant?: 'card' | 'compact';
}

const MODE_META = {
  quick: {
    badge: '~1s',
    badgeColor: 'success' as const,
    label: 'Quick',
    desc: 'Vector search only',
    sub: 'No LLM calls — fastest possible',
    border: 'success.main',
    bg: 'rgba(76,175,80,0.07)',
    iconColor: 'success.main',
    compactExplainer: '⚡ Vector search only – fastest, no LLM',
  },
  advanced: {
    badge: '~10s',
    badgeColor: 'warning' as const,
    label: 'Advanced',
    desc: 'LLM judge',
    sub: 'Mistral judge picks the best match — most accurate',
    border: 'warning.main',
    bg: 'rgba(237,108,2,0.07)',
    iconColor: 'warning.dark',
    compactExplainer: '🔬 LLM judge picks the best match – most accurate',
  },
};

const MODE_ORDER: ClassificationMode[] = ['quick', 'advanced'];

const ICONS: Record<ClassificationMode, React.ReactElement> = {
  quick: <QuickIcon fontSize="small" />,
  advanced: <AdvancedIcon fontSize="small" />,
};

const ClassificationModeToggle: React.FC<ClassificationModeToggleProps> = ({
  value,
  onChange,
  variant = 'compact',
}) => {
  if (variant === 'card') {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ mb: 2, display: 'block', letterSpacing: 1, textTransform: 'uppercase' }}
        >
          Classification Mode
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2.5 }}>
          {MODE_ORDER.map((mode) => {
            const m = MODE_META[mode];
            const selected = value === mode;
            const CardIcon = mode === 'quick' ? QuickIcon : AdvancedIcon;
            return (
              <Box
                key={mode}
                onClick={() => onChange(mode)}
                sx={{
                  p: 2.5,
                  borderRadius: 2.5,
                  border: '2px solid',
                  borderColor: selected ? m.border : 'grey.200',
                  bgcolor: selected ? m.bg : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  userSelect: 'none',
                  '&:hover': {
                    borderColor: m.border,
                    bgcolor: m.bg,
                    transform: 'translateY(-1px)',
                    boxShadow: selected ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1.5,
                  }}
                >
                  <CardIcon
                    sx={{
                      fontSize: 30,
                      color: selected ? m.iconColor : 'action.disabled',
                    }}
                  />
                  <Chip
                    label={m.badge}
                    size="small"
                    color={selected ? m.badgeColor : 'default'}
                    sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }}
                  />
                </Box>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  color={selected ? 'text.primary' : 'text.secondary'}
                  sx={{ mb: 0.5 }}
                >
                  {m.label}
                </Typography>
                <Typography
                  variant="body2"
                  color={selected ? 'text.primary' : 'text.disabled'}
                  fontWeight={500}
                  display="block"
                  sx={{ mb: 0.25 }}
                >
                  {m.desc}
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block">
                  {m.sub}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={700}
        sx={{ minWidth: 'fit-content' }}
      >
        Mode:
      </Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_: React.MouseEvent<HTMLElement>, val: ClassificationMode | null) =>
          val && onChange(val)
        }
        size="small"
        color="primary"
      >
        {MODE_ORDER.map((mode) => (
          <ToggleButton key={mode} value={mode} sx={{ gap: 0.5, px: 1.5 }}>
            {ICONS[mode]}
            {MODE_META[mode].label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary">
        {MODE_META[value].compactExplainer}
      </Typography>
    </Box>
  );
};

export default ClassificationModeToggle;
